import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "defaultOpen", "fixedChildren", "hideDefaultTooltip", "initialWidth", "matchContentWidth", "maxWidth", "onClick", "onOpenChange", "onTruncatedChange", "open", "placement", "reverse", "role", "title", "tooltip", "useFlex"];
import { cancelSchedulerCallback, requestSchedulerCallback } from '../../utils/Timers';
import { memo, useEffect, useRef, useState } from 'react';
import { HiddenMeasure } from '../../utils/HiddenMeasure';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { filterProps } from '../../../utils/filterProps';
import { overflowsX } from '../../utils/Overflows';
import styled from 'foundations-theming/utils';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useInputStaticLabelContext } from '../InputStaticLabel/InputStaticLabelProvider';
import { deepMerge } from '../../utils/deepMerge';
import { MIN_WIDTH } from '../../../constants/nonTokenisedValues';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MUTATION_OBSERVER_CONFIG = {
  childList: true,
  subtree: true,
  characterData: true
};
const ReversedTruncateStringEllipse = styled.span.withConfig({
  displayName: "TruncateString__ReversedTruncateStringEllipse"
})(["user-select:none;float:left;"]);
const ReversedTruncateStringTail = styled.span.withConfig({
  displayName: "TruncateString__ReversedTruncateStringTail"
})(({
  $isTruncated
}) => {
  return Object.assign({
    overflow: 'hidden'
  }, $isTruncated && {
    float: 'right',
    width: 'calc(100% - 1em)'
  });
});
const ReversedTruncateStringContent = styled.span.withConfig({
  displayName: "TruncateString__ReversedTruncateStringContent"
})(({
  $isTruncated
}) => {
  return Object.assign({}, $isTruncated && {
    float: 'right'
  }, !$isTruncated && {
    display: 'block'
  });
});
export const TruncateStringContainer = styled.span.withConfig({
  displayName: "TruncateString__TruncateStringContainer"
})(({
  $computedMaxWidth,
  $cssStyles,
  $hideDefaultTooltip,
  $initialWidth,
  $isTruncated,
  $maxWidth,
  $tooltip,
  $useFlex,
  $styleOverrides,
  theme
}) => {
  const hasTooltip = $tooltip && $isTruncated && !$hideDefaultTooltip;
  return deepMerge(Object.assign({
    display: 'table',
    minWidth: MIN_WIDTH.TRUNCATE_STRING,
    tableLayout: 'fixed',
    width: $initialWidth
  }, $useFlex && {
    alignItems: 'baseline',
    display: 'flex'
  }, hasTooltip && {
    cursor: 'help',
    '&:focus': {
      outline: `${theme.borderWidth['100']} dotted`
    },
    'a &, button &': {
      cursor: 'pointer'
    }
  }, $computedMaxWidth && Object.assign({
    maxWidth: $computedMaxWidth
  }, $maxWidth && {
    width: 'unset'
  })), $styleOverrides, $cssStyles);
});
const TruncateStringInner = styled.span.withConfig({
  displayName: "TruncateString__TruncateStringInner"
})(({
  $computedMaxWidth,
  $maxWidth,
  $useFlex
}) => {
  return Object.assign({
    hyphens: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    display: 'table-cell',
    verticalAlign: 'middle'
  }, $useFlex && {
    display: 'block',
    minWidth: MIN_WIDTH.TRUNCATE_STRING,
    width: 'auto'
  }, $computedMaxWidth && Object.assign({
    maxWidth: $computedMaxWidth
  }, $maxWidth && {
    width: 'unset'
  }));
});
export const InternalTruncateString = _ref => {
  var _inputStaticLabelCont;
  let {
      children,
      defaultOpen = false,
      fixedChildren,
      hideDefaultTooltip = false,
      initialWidth = '100%',
      matchContentWidth = false,
      maxWidth,
      onClick,
      onOpenChange,
      onTruncatedChange,
      open,
      placement,
      reverse = false,
      role,
      title,
      tooltip = true,
      useFlex = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const inputStaticLabelContext = useInputStaticLabelContext();
  const [contentWidth, setContentWidth] = useState(undefined);
  const [isTruncated, setIsTruncated] = useState(false);
  const wrapperRef = useRef(null);
  const measureRef = useRef(null);
  const tailRef = useRef(null);

  // Workaround for a truly bizarre Safari bug (#5825); still replicable as of Safari 13.0.5
  const keyRef = useRef(0);
  const hasMaxWidth = maxWidth != null;
  const hasMaxWidthRef = useRef(hasMaxWidth);
  if (hasMaxWidthRef.current !== hasMaxWidth) {
    keyRef.current += 1;
    hasMaxWidthRef.current = hasMaxWidth;
  }
  const key = keyRef.current;
  const updateTruncatedState = () => {
    const wrapperEl = wrapperRef.current;
    const tailEl = tailRef.current;
    if (matchContentWidth) {
      const measureEl = measureRef.current;
      if (measureEl instanceof HTMLElement && measureEl !== null) {
        setContentWidth(measureEl.clientWidth + 1);
      }
    }
    const newIsTruncated = reverse ? overflowsX(tailEl, wrapperEl) : overflowsX(wrapperEl);
    setIsTruncated(currentIsTruncated => {
      if (newIsTruncated !== currentIsTruncated) {
        if (onTruncatedChange) onTruncatedChange(syntheticEvent(newIsTruncated));
        return newIsTruncated;
      }
      return currentIsTruncated;
    });
  };

  // Using a scheduler callback ensures that measurement is batched, preventing extra layout costs
  const delayTimeoutRef = useRef(null);
  const delayedUpdateTruncatedState = () => {
    if (delayTimeoutRef.current) {
      cancelSchedulerCallback(delayTimeoutRef.current);
    }
    delayTimeoutRef.current = requestSchedulerCallback(updateTruncatedState);
  };

  // Add mount/unmount behavior
  useEffect(() => {
    const resizeObserver = new ResizeObserver(delayedUpdateTruncatedState);
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }
    return () => {
      if (delayTimeoutRef.current) {
        cancelSchedulerCallback(delayTimeoutRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let mutationObserver;
    if (wrapperRef.current) {
      mutationObserver = new MutationObserver(() => {
        delayedUpdateTruncatedState();
      });
      mutationObserver.observe(wrapperRef.current, MUTATION_OBSERVER_CONFIG);
    }
    return () => {
      if (delayTimeoutRef.current) {
        cancelSchedulerCallback(delayTimeoutRef.current);
      }
      if (mutationObserver) mutationObserver.disconnect();
    };
  }, [matchContentWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  // When maxWidth is set, we need to set maxWidth on both the outer container and the inner
  // wrapper. We also need to set width to "unset" (if we aren't using matchContentWidth) in
  // order to prevent the table column from collapsing to 0 due to the `width: 100%` rule.
  const computedMaxWidth = maxWidth && contentWidth ? Math.min(maxWidth, contentWidth) : maxWidth || contentWidth;
  const renderedContent = reverse ? /*#__PURE__*/_jsxs("span", {
    children: [isTruncated && /*#__PURE__*/_jsx(ReversedTruncateStringEllipse, {
      children: "\u2026"
    }), /*#__PURE__*/_jsx(ReversedTruncateStringTail, {
      $isTruncated: isTruncated,
      children: /*#__PURE__*/_jsx(ReversedTruncateStringContent, {
        $isTruncated: isTruncated,
        ref: tailRef,
        children: children
      })
    })]
  }) : /*#__PURE__*/_jsx("span", {
    children: children
  });
  const computedTooltip = tooltip === true ? children : tooltip;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Tooltip, {
      open: open,
      defaultOpen: defaultOpen,
      onOpenChange: onOpenChange,
      disabled: isTruncated === false || hideDefaultTooltip,
      placement: placement,
      title: computedTooltip,
      children: /*#__PURE__*/_createElement(TruncateStringContainer, Object.assign({}, domProps, {
        $computedMaxWidth: computedMaxWidth,
        $cssStyles: cssStyles,
        $styleOverrides: (_inputStaticLabelCont = inputStaticLabelContext === null || inputStaticLabelContext === void 0 ? void 0 : inputStaticLabelContext.truncateStringStyleOverrides) !== null && _inputStaticLabelCont !== void 0 ? _inputStaticLabelCont : {},
        $hideDefaultTooltip: hideDefaultTooltip,
        $initialWidth: initialWidth,
        $isTruncated: isTruncated,
        $maxWidth: maxWidth,
        $tooltip: !!computedTooltip,
        $useFlex: useFlex,
        "data-content": true,
        key: key,
        onClick: onClick,
        role: role,
        title: title
      }), /*#__PURE__*/_jsx(TruncateStringInner, {
        $computedMaxWidth: computedMaxWidth,
        $maxWidth: maxWidth,
        $useFlex: useFlex,
        ref: wrapperRef,
        children: renderedContent
      }), fixedChildren)
    }), matchContentWidth && /*#__PURE__*/_jsx(HiddenMeasure, {
      ref: measureRef,
      children: children
    })]
  });
};
export const TruncateString = /*#__PURE__*/memo(InternalTruncateString);
TruncateString.displayName = 'TruncateString';